import * as Sentry from '@sentry/vue';
import i18n from '@/plugins/i18n';
import enMessages from '@/lang/en';
import { isDevEnv } from '@/constants/config';
import {
  fetchRemoteTranslations,
  isRemoteTranslationsEnabled,
  type RemoteTranslations,
} from '@/services/translationsService';

/**
 * Runtime loader for translations served by survey-builder-glue.
 *
 * Fetched strings are deep-merged over the bundled messages of the same locale,
 * so for a key `k` in locale `L` the resolution order is:
 *   SBG `L` → bundled `L` → vue-i18n implicit parent → bundled `en`.
 */

type NestedMessages = { [key: string]: string | NestedMessages };

const SOURCE_LOCALE = 'en';
const PLACEHOLDER_RE = /\{\s*([^{}]*?)\s*\}/g;

/** Session cache: one entry per locale (hyphen form). */
const cache = new Map<string, Promise<void>>();
/** Locales whose fetch completed successfully. */
const loaded = new Set<string>();

let enFlat: Map<string, string> | null = null;

const flatten = (obj: Record<string, unknown>, prefix = '', out = new Map<string, string>()) => {
  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'string') {
      out.set(path, value);
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      flatten(value as Record<string, unknown>, path, out);
    }
  }
  return out;
};

const getEnFlat = (): Map<string, string> => {
  if (!enFlat) enFlat = flatten(enMessages as Record<string, unknown>);
  return enFlat;
};

export const normaliseLocale = (locale: string): string => locale.replace(/_/g, '-');

const isSourceLocale = (key: string): boolean => key.toLowerCase() === SOURCE_LOCALE;

const placeholders = (value: string): string[] =>
  [...new Set(Array.from(value.matchAll(PLACEHOLDER_RE), m => m[1]))].sort();

const hasStrayBraces = (value: string): boolean => /[{}]/.test(value.replace(PLACEHOLDER_RE, ''));

const sameList = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((item, i) => item === b[i]);

type DropReason = 'unknownKey' | 'placeholderMismatch' | 'specialCharacter';

const dropReason = (key: string, value: string): DropReason | null => {
  const source = getEnFlat().get(key);
  if (source === undefined) return 'unknownKey';

  if (!sameList(placeholders(value), placeholders(source))) return 'placeholderMismatch';
  if (hasStrayBraces(value) && !hasStrayBraces(source)) return 'placeholderMismatch';

  // vue-i18n reads `@` as a linked message and `|` as a plural separator.
  for (const char of ['@', '|']) {
    if (value.includes(char) && !source.includes(char)) return 'specialCharacter';
  }

  return null;
};

/** Per-key safety filter (§5.4). */
export const filterRemoteTranslations = (locale: string, messages: RemoteTranslations): RemoteTranslations => {
  const safe: RemoteTranslations = {};
  const drops: Record<DropReason, number> = { unknownKey: 0, placeholderMismatch: 0, specialCharacter: 0 };
  const dev = isDevEnv();

  for (const [key, value] of Object.entries(messages)) {
    const reason = dropReason(key, value);
    if (reason) {
      drops[reason]++;
      dev && console.warn(`[translations] Dropped "${locale}" key "${key}" (${reason})`, value);
      continue;
    }
    safe[key] = value;
  }

  const total = drops.unknownKey + drops.placeholderMismatch + drops.specialCharacter;
  if (total > 0 && !dev) {
    Sentry.addBreadcrumb({
      category: 'i18n',
      level: 'warning',
      message: 'Remote translations dropped',
      data: { locale, total, ...drops },
    });
  }

  return safe;
};

export const unflatten = (flat: RemoteTranslations): NestedMessages => {
  const root: NestedMessages = {};
  for (const [path, value] of Object.entries(flat)) {
    const parts = path.split('.');
    let node = root;
    for (let i = 0; i < parts.length - 1; i++) {
      const next = node[parts[i]];
      if (typeof next !== 'object') node[parts[i]] = {};
      node = node[parts[i]] as NestedMessages;
    }
    node[parts[parts.length - 1]] = value;
  }
  return root;
};

/**
 * Synchronous check: `true` for `en`, when the feature is off, or when the
 * locale has already been fetched successfully.
 */
export const isLocaleLoaded = (locale: string): boolean => {
  if (!locale) return true;
  const key = normaliseLocale(locale);
  if (isSourceLocale(key) || !isRemoteTranslationsEnabled()) return true;
  return loaded.has(key);
};

/**
 * Fetches one locale from SBG (once per session) and merges it over the
 * bundled messages. Never rejects.
 */
export const ensureLocaleLoaded = (locale: string): Promise<void> => {
  if (!locale) return Promise.resolve();
  const key = normaliseLocale(locale);
  if (isSourceLocale(key) || !isRemoteTranslationsEnabled()) return Promise.resolve();

  const cached = cache.get(key);
  if (cached) return cached;

  const pending = (async () => {
    try {
      const result = await fetchRemoteTranslations(key);
      if (!result.ok) {
        cache.delete(key); // retry next time this locale is needed
        return;
      }

      const safe = filterRemoteTranslations(key, result.messages);
      if (Object.keys(safe).length > 0) {
        // Keyed by *this* locale, so late responses never touch the active one.
        i18n.global.mergeLocaleMessage(key as any, unflatten(safe) as any);
      }
      loaded.add(key);
    } catch (error) {
      cache.delete(key);
      console.warn(`[translations] Could not apply remote translations for "${key}"`, error);
    }
  })();

  cache.set(key, pending);
  return pending;
};

/** Test helper: forget every fetched locale. */
export const resetRemoteTranslations = (): void => {
  cache.clear();
  loaded.clear();
};

export const useRemoteTranslations = () => ({
  ensureLocaleLoaded,
  isLocaleLoaded,
});

